'use client';

import { CSSPropsVars } from '@/interfaces/types/CSSPropsVars';
import { colorBrightness } from '@/packages/color/colorBrightness';
import { requestYmmModal } from '@/packages/sxm/ymm/ymmModalEvent';
import cn from 'classnames';
import { MouseEventHandler, ReactNode, Ref, forwardRef } from 'react';
import { Link } from '../Link/Link';
import styles from './button.module.scss';
import SXMLink from '@/src/shared-components/SXMLink';

export type ButtonFitting = 'hug-contents' | 'fill-container';
export type ButtonSize = 'large' | 'small' | 'x-small';
export type ButtonColorSet = 'base' | 'neutral' | 'inverse' | 'overlay' | 'subset-sxm-blue';
export type ButtonVariants = 'primary' | 'secondary' | 'tertiary' | 'custom';

export interface ButtonProps {
    ariaControls?: string;
    ariaExpanded?: boolean;
    ariaHaspopup?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
    ariaLabel?: string;
    children?: ReactNode;
    className?: string;
    color?: string;
    colorSet?: ButtonColorSet;
    dataCy?: string;
    disabled?: boolean;
    disableHoverGrow?: boolean;
    fitting?: ButtonFitting;
    href?: string;
    iconButton?: boolean;
    linkTarget?: string;
    onClick?: MouseEventHandler;
    size?: ButtonSize;
    tertiaryNoPadding?: boolean;
    type: 'submit' | 'button';
    variant: ButtonVariants;
    linkType?: string;
    [name: `data-${string}`]: string;
    buttonType?: string;
    inheritParams?: boolean;
}
export const Button = forwardRef(function (
    {
        ariaControls,
        ariaExpanded,
        ariaHaspopup,
        ariaLabel,
        children,
        className,
        color,
        colorSet = 'base',
        dataCy,
        disabled,
        disableHoverGrow,
        fitting,
        href,
        iconButton,
        linkTarget,
        onClick,
        size,
        tertiaryNoPadding,
        type,
        buttonType,
        variant = 'primary',
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        linkType,
        inheritParams,
        ...dataProps
    }: ButtonProps,
    ref: Ref<HTMLElement>,
): React.JSX.Element {
    const buttonStyles: CSSPropsVars = {};
    const classNames = cn(styles.button, className, {
        [styles[variant]]: variant,
        [styles[colorSet]]: colorSet,
        [styles.disabled]: disabled,
        [styles.disableHoverGrow]: disableHoverGrow,
        [styles.iconButton]: iconButton,
        [styles.tertiaryNoPadding]: variant === 'tertiary' && tertiaryNoPadding,
        ...(size ? { [styles[size]]: size } : {}),
        ...(fitting ? { [styles[fitting]]: fitting } : {}),
    });
    if (variant === 'custom' && color) {
        const brightness = colorBrightness(color);
        buttonStyles['--base-button-primary-surface'] = color;
        buttonStyles['--base-button-primary-surface-on-hover'] = color;
        buttonStyles['--base-button-primary-contrast'] = brightness > 128 ? 'var(--black)' : 'var(--white)';
        buttonStyles['--base-button-primary-contrast-on-hover'] = brightness > 128 ? 'var(--black)' : 'var(--white)';
        buttonStyles['--base-button-primary-border'] = color;
    }

    const LinkComponent = inheritParams ? SXMLink : Link;

    /**
     * if we have an href, we'll treat this as an anchor tag styled as a button.
     * if no href, we'll need a <button> to use in sweeps.
     */

    if (href || href === '') {
        if (buttonType === 'YMM') {
            return (
                <Link
                    className={classNames}
                    href="#ymmModal"
                    target={linkTarget === '_blank' ? '_blank' : ''}
                    aria-label={ariaLabel}
                    aria-expanded={ariaExpanded}
                    aria-controls={ariaControls}
                    onClick={(e) => {
                        e.preventDefault();
                        requestYmmModal();
                    }}
                    style={buttonStyles}
                    {...dataProps}
                    dataCy={dataCy}
                >
                    <div className={styles.content}>{children}</div>
                </Link>
            );
        } else {
            return (
                <LinkComponent
                    className={classNames}
                    href={href}
                    {...(inheritParams && { inheritParams })}
                    target={linkTarget === '_blank' ? '_blank' : ''}
                    aria-label={ariaLabel}
                    aria-expanded={ariaExpanded}
                    aria-controls={ariaControls}
                    onClick={onClick}
                    style={buttonStyles}
                    {...dataProps}
                    dataCy={dataCy}
                >
                    <div className={styles.content}>{children}</div>
                </LinkComponent>
            );
        }
    } else {
        return (
            <button
                ref={ref as Ref<HTMLButtonElement>}
                type={type}
                onClick={onClick}
                className={classNames}
                aria-label={ariaLabel}
                aria-expanded={ariaExpanded}
                aria-controls={ariaControls}
                aria-haspopup={ariaHaspopup}
                disabled={disabled}
                aria-disabled={disabled}
                style={buttonStyles}
                {...dataProps}
                data-cy={dataCy || 'atoms-button'}
            >
                <span className={styles.content}>{children}</span>
            </button>
        );
    }
});
Button.displayName = 'Button';
